import {useLearningStore} from "~/store/learning/learningStore";
import {WebContainer} from "@webcontainer/api";
import {useLearningService} from "~/composables/service/useLearningService";
import {LearningHistorySubType, LearningHistoryType} from "~~/package/mapper/learning/model/LearningHistoryType";

export const useWebContainerService = () => {
    const learningStore = useLearningStore();
    const learningService = useLearningService();

    const startWebContainer = async (isOnlyBoot = false) => {
        learningStore.isFrontRun = true;
        console.time("startWebContainer")
        if (!learningStore.frontLearning.webContainer) {
            learningStore.frontLearning.webContainer = await WebContainer.boot();
        }

        if(isOnlyBoot) return;
        const dir = await (learningStore.frontLearning.webContainer as WebContainer).fs.readdir('./');
        if(dir.length > 0) {
            const resetDirectoryExitCode = await resetDirectory();
            if(resetDirectoryExitCode !== 0) console.error('reset directory failed');
        }

        await (learningStore.frontLearning.webContainer as WebContainer).mount(learningStore.directory.tree);

        if(Object.keys(learningStore.frontLearning.nodeModules).length > 0) {
            await installZipModule();
        }

        console.timeLog("startWebContainer", 'npm install')
        const exitCode = await installDependencies();
        if (exitCode !== 0) {
            throw new Error('Installation failed');
        }
        console.timeLog("startWebContainer", 'npm install end')

        const changeFolderPermissionsExitCode = await changeFolderPermissions();
        if (changeFolderPermissionsExitCode !== 0) {
            throw new Error('changeFolderPermissionsExitCode failed');
        }

        runWebContainer();
    }

    const killAllProcess = () => {
        learningStore.frontLearning.webContainerProcessList.forEach(process => {
            process.kill();
        });
        learningStore.frontLearning.webContainerProcessList = []
    }

    const runWebContainer = async (withReMount: boolean = false) => {
        learningStore.isFrontRun = true;
        if (withReMount) {
            await (learningStore.frontLearning.webContainer as WebContainer).spawn('rm', ['-r', '*']);
            await (learningStore.frontLearning.webContainer as WebContainer).mount(learningStore.directory.tree);
        }
        killAllProcess();
        startDevServer();
    }

    const installZipModule = async () => {
        await (learningStore.frontLearning.webContainer as WebContainer).fs.mkdir("node_modules");
        console.timeLog("startWebContainer", 'start mount')
        for(const key of Object.keys(learningStore.frontLearning.nodeModules)) {
            try {
                await (learningStore.frontLearning.webContainer as WebContainer).fs.mkdir(`node_modules/${key}`);
                await (learningStore.frontLearning.webContainer as WebContainer).mount(
                    learningStore.frontLearning.nodeModules[key] as Uint8Array,
                    {mountPoint: `node_modules/${key}`}
                );
            } catch (e) {
                console.error('error key :', key)
                console.error(e);
            }
        }
        console.timeLog("startWebContainer", 'end mount')
    }

    const changeFolderPermissions = async ()=> {
        const changeFolderPermissions = await (learningStore.frontLearning.webContainer as WebContainer).spawn('chmod', ['-R', '755', './node_modules']);
        changeFolderPermissions.output.pipeTo(new WritableStream({
            write(data) {
                console.log(data);
            }
        }));
        return changeFolderPermissions.exit;
    }

    const resetDirectory = async ()=> {
        const resetDirectoryProcess = await (learningStore.frontLearning.webContainer as WebContainer).spawn('sh', ['-c', 'rm -r *']);
        resetDirectoryProcess.output.pipeTo(new WritableStream({
            write(data) {
                console.log(data);
            }
        }));
        return resetDirectoryProcess.exit;
    }

    const installDependencies = async () => {
        let installProcess
        if(learningStore.directory.tree['yarn.lock']) {
            installProcess = await (learningStore.frontLearning.webContainer as WebContainer).spawn('yarn', ['install']);
        } else {
            installProcess = await (learningStore.frontLearning.webContainer as WebContainer).spawn('npm', ['install']);
        }
        installProcess.output.pipeTo(new WritableStream({
            write(data) {
                learningStore.frontLearning.terminalStreamData.push(data);
            }
        }));

        learningStore.frontLearning.webContainerProcessList.push(installProcess)
        return installProcess.exit;
    }

    const startDevServer = async () => {
        if(!learningStore.frontLearning.webContainer) return;

        const devProcess = await (learningStore.frontLearning.webContainer as WebContainer).spawn('npm', ['run', 'dev'])
        devProcess.output.pipeTo(new WritableStream({
            write(data) {
                learningStore.frontLearning.terminalStreamData.push(data);
            }
        }));
        learningStore.frontLearning.webContainerProcessList.push(devProcess)

        const serverReadyUnsubscribe = (learningStore.frontLearning.webContainer as WebContainer).on('server-ready', (port, url) => {
            console.timeEnd("startWebContainer")
            if(learningStore.frontLearning.webContainerTestProcessList.length === 0) {
                learningStore.frontLearning.defaultLocation = url;
                learningStore.frontLearning.currentLocation = url;
            }
            learningStore.isFrontRun = false;
        });


        const errorUnsubscribe = (learningStore.frontLearning.webContainer as WebContainer).on('error', (error) => {
            throw new Error(`webcontainer\n${error}`)
        });

        learningStore.frontLearning.unsubscribeList = [serverReadyUnsubscribe, errorUnsubscribe]
    }

    // TODO front 자동 채점 명령어
    const generateAutoGradingResult = async () => {
        if(!learningStore.frontLearning.webContainer) return;
        const resultProcess= await (learningStore.frontLearning.webContainer as WebContainer).spawn('npm', ['run','test:output']);
        resultProcess.output.pipeTo(new WritableStream({
            write(data) {
                console.log(data);
            }
        }));

        learningStore.frontLearning.webContainerTestProcessList.push(resultProcess);
        return resultProcess;
    }

    const destroyProcess = () => {
        if (learningStore.frontLearning.webContainer) {
            learningStore.frontLearning.unsubscribeList.forEach(unsubscribe => unsubscribe());

            learningStore.frontLearning.webContainerProcessList.forEach((prosess) => {
                prosess.kill()
            })
            learningStore.frontLearning.webContainerTestProcessList.forEach((prosess) => {
                prosess.kill()
            })

            learningStore.frontLearning.defaultLocation = ""
            learningStore.frontLearning.currentLocation = ""
            learningStore.frontLearning.unsubscribeList = []
            learningStore.frontLearning.webContainerProcessList = []
            learningStore.frontLearning.webContainerTestProcessList = []
            learningStore.frontLearning.nodeModules = {}
            learningStore.frontLearning.terminalStreamData = []
            learningStore.frontLearning.isShowToastMessage = false;
        }
    }

    const writeModifiedFile = async (path: string, contents: string | Uint8Array) => {
        if (!learningStore.frontLearning.webContainer) return;
        return await learningStore.frontLearning.webContainer.fs.writeFile(path, contents);
    };

    return {
        startWebContainer,
        generateAutoGradingResult,
        destroyProcess,
        writeModifiedFile,
        runWebContainer
    };
}
